import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";

import type { DashboardAlertPreviewItem } from "@/features/deployment-dashboard/overview/model/dashboard-monitoring-data";
import { toStatusBadgeSeverity } from "@/features/deployment-dashboard/widgets/panels/active-notifications-preview/active-notifications-preview-panel.utils";
import { StatusBadge } from "@/shared/ui";
import { LocationIcon } from "@/shared/ui/console-icons";

interface ActiveNotificationCardProps {
  notification: DashboardAlertPreviewItem;
  onSelect: () => void;
}

/** Linked notification preview with the operational context kept in a bottom metadata row. */
export function ActiveNotificationCard({ notification, onSelect }: ActiveNotificationCardProps) {
  return (
    <ButtonBase
      onClick={onSelect}
      aria-label={`Open alert: ${notification.title}`}
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        width: "100%",
        minHeight: 96,
        textAlign: "left",
        px: 1.5,
        py: 1.25,
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: alpha(theme.palette.background.paper, 0.3),
        color: "inherit",
        textDecoration: "none",
        "&:hover": {
          backgroundColor: alpha(theme.palette.text.primary, 0.04),
          borderColor: alpha(theme.palette.common.white, 0.2),
        },
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      })}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography variant="subtitle2">{notification.title}</Typography>
        <ChevronRightIcon
          aria-hidden="true"
          sx={{ fontSize: "1rem", opacity: 0.35, flexShrink: 0, mt: 0.25 }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          mt: "auto",
          pt: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <LocationIcon aria-hidden="true" sx={{ fontSize: "0.75rem", color: "text.secondary" }} />
          <Typography variant="caption" color="text.secondary">
            {notification.zone}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <AccessTimeIcon
            aria-hidden="true"
            sx={{ fontSize: "0.75rem", color: "text.secondary" }}
          />
          <Typography variant="caption" color="text.secondary">
            {notification.detectedAt}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ mt: 0.75 }}>
        <StatusBadge
          compact
          severity={toStatusBadgeSeverity(notification.severity)}
          label={notification.severity.toUpperCase()}
        />
      </Box>
    </ButtonBase>
  );
}
